import { EnhancedMarkdown } from '@/components/ui/EnhancedMarkdown'
import { convertMarkdownToDocx, convertMarkdownToPdf } from '@/lib/docx-utils'
import { Copy, Download, ExternalLink } from 'lucide-react'
import { toast } from 'react-toastify'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import type { WorkflowConfig } from 'sprites-types/workflow/core'
import type { RenderingConfig } from 'sprites-types/workflow/tools/common'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import { DynamicContentRenderer, HtmlContentViewer } from '../shared/DynamicContentRenderer'
import { ObjectDisplay } from '../shared/ObjectDisplay'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import {
  csvToMarkdownTable,
  extractRenderingSchema,
  inferRawPresentation,
  inferredContentToMarkdown,
  processRawOutputs,
  resolveListTemplateVariables,
  resolveTemplateVariables,
} from '../shared/renderingUtils'

async function downloadAsDocx(content: string, filename: string = 'llm-response.docx') {
  try {
    const blob = await convertMarkdownToDocx(content)
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.click()
    URL.revokeObjectURL(url)
    toast.success('DOCX file downloaded successfully')
  } catch (error) {
    console.error('Error creating DOCX:', error)
    toast.error('Failed to download DOCX file')
  }
}

async function downloadAsPdf(content: string, filename: string = 'llm-response.pdf') {
  try {
    const blob = await convertMarkdownToPdf(content)
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.click()
    URL.revokeObjectURL(url)
    toast.success('PDF file downloaded successfully')
  } catch (error) {
    console.error('Error creating PDF:', error)
    toast.error('Failed to download PDF file')
  }
}

function NativeLlmResponseTextDisplayComponent({
  nodeExecution,
  workflowConfig,
  onOpenPanel,
}: WorkflowToolComponentProps) {
  const renderingSchema = extractRenderingSchema(nodeExecution)

  // If no rendering schema provided, show smart raw outputs
  if (!renderingSchema || renderingSchema.length === 0) {
    const inferredList = processRawOutputs(nodeExecution, workflowConfig)

    const rawContextActions = [
      {
        id: 'copy-raw',
        label: 'Copy raw content',
        icon: <Copy className="h-4 w-4" />,
        onClick: () => {
          const content = inferredList
            .map((inf, i) => `# Output ${i + 1}\n` + inferredContentToMarkdown(inf))
            .join('\n\n---\n\n')
          navigator.clipboard
            .writeText(content)
            .then(() => toast.success('Raw content copied to clipboard'))
            .catch(() => toast.error('Failed to copy raw content'))
        },
      },
      {
        id: 'download-raw-docx',
        label: 'Download as DOCX',
        icon: <Download className="h-4 w-4" />,
        onClick: async () => {
          const content = inferredList
            .map((inf, i) => {
              const header = `# Output ${i + 1}`
              return `${header}\n\n${inferredContentToMarkdown(inf)}`
            })
            .join('\n\n---\n\n')
          const filename = `llm-response-raw-${nodeExecution.nodeId}-${Date.now()}.docx`
          await downloadAsDocx(content, filename)
        },
      },
      {
        id: 'download-raw-pdf',
        label: 'Download as PDF',
        icon: <Download className="h-4 w-4" />,
        onClick: async () => {
          const content = inferredList
            .map((inf, i) => {
              const header = `# Output ${i + 1}`
              return `${header}\n\n${inferredContentToMarkdown(inf)}`
            })
            .join('\n\n---\n\n')
          const filename = `llm-response-raw-${nodeExecution.nodeId}-${Date.now()}.pdf`
          await downloadAsPdf(content, filename)
        },
      },
    ]

    return (
      <ContextMenuWrapper actions={rawContextActions}>
        <div className="space-y-4">
          {inferredList.map((inferred, idx) =>
            inferred.type === 'markdown' ? (
              <EnhancedMarkdown
                key={idx}
                style={{ backgroundColor: 'transparent', color: '#374151' }}
                className="prose prose-sm max-w-none"
              >
                {inferred.content}
              </EnhancedMarkdown>
            ) : inferred.type === 'object' && inferred.data ? (
              <div
                key={idx}
                className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]"
              >
                <div className="group flex items-center gap-4 bg-white px-4 py-3">
                  <div className="flex-shrink-0">
                    <div className="h-5 w-5 rounded bg-gradient-to-br from-blue-500 to-purple-500"></div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-gray-900">LLM Response</div>
                    <div className="text-xs font-medium tracking-wide text-gray-500">
                      Object output {inferredList.length > 1 ? `#${idx + 1}` : ''}
                    </div>
                  </div>
                </div>
                <div className="px-4 py-3">
                  <ObjectDisplay data={inferred.data} />
                </div>
              </div>
            ) : (
              <div
                key={idx}
                className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]"
              >
                <div className="group flex items-center gap-4 bg-white px-4 py-3">
                  <div className="flex-shrink-0">
                    <div className="h-5 w-5 rounded bg-gradient-to-br from-blue-500 to-purple-500"></div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-gray-900">LLM Response</div>
                    <div className="text-xs font-medium tracking-wide text-gray-500">
                      Raw output {inferredList.length > 1 ? `#${idx + 1}` : ''}
                    </div>
                  </div>
                </div>
                <div className="px-4 py-3">
                  {inferred.type === 'html' && <HtmlContentViewer content={inferred.content} />}
                  {inferred.type === 'csv' && (
                    <EnhancedMarkdown
                      style={{ backgroundColor: 'transparent', color: '#374151' }}
                      className="prose prose-sm max-w-none"
                    >
                      {csvToMarkdownTable(inferred.content)}
                    </EnhancedMarkdown>
                  )}
                  {inferred.type === 'url' && (
                    <div className="rounded-2xl border border-[#E0E0E0] bg-[#F3F3F3] p-4">
                      <div className="flex items-center gap-2">
                        <ExternalLink className="h-4 w-4" />
                        <a
                          href={inferred.content}
                          className="text-blue-600 hover:underline"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {inferred.content}
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          )}
        </div>
      </ContextMenuWrapper>
    )
  }

  return (
    <div className="space-y-4">
      {renderingSchema.map((config, index) => (
        <DynamicContentRenderer
          key={index}
          type={config.type}
          content={config.content}
          outputs={nodeExecution.outputs}
          nodeId={nodeExecution.nodeId}
          onOpenPanel={onOpenPanel}
          enableDownloads={true}
        />
      ))}
    </div>
  )
}

function downloadLlmResponse(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function nativeLlmResponseTextToMarkdown(
  nodeExecution: NodeExecution,
  workflowConfig?: WorkflowConfig
): string {
  try {
    const renderingSchema = extractRenderingSchema(nodeExecution)
    const sections: string[] = []

    if (!renderingSchema || renderingSchema.length === 0) {
      const inferredList = processRawOutputs(nodeExecution, workflowConfig)

      if (inferredList.length === 0) {
        sections.push(
          `## ${nodeExecution.nodeId.replace('-', ' ').replace(/\b\w/g, (char) => char.toUpperCase())}\n`
        )
        const inferred = inferRawPresentation(nodeExecution.outputs)
        sections.push(inferredContentToMarkdown(inferred))
      } else {
        inferredList.forEach((inferred, index) => {
          if (index > 0) sections.push('\n---\n')
          sections.push(inferredContentToMarkdown(inferred))
        })
      }
    } else {
      const resolvedRenderingConfigs = renderingSchema.map((config: RenderingConfig) => ({
        ...config,
        content:
          config.type === 'list'
            ? resolveListTemplateVariables(
                config.content,
                nodeExecution.outputs,
                nodeExecution.nodeId
              )
            : resolveTemplateVariables(config.content, nodeExecution.outputs, nodeExecution.nodeId),
      }))

      resolvedRenderingConfigs.forEach((config, index) => {
        if (index > 0) sections.push('\n---\n')

        switch (config.type) {
          case 'markdown':
          case 'list':
            sections.push(config.content)
            break
          case 'html':
            sections.push('```html')
            sections.push(config.content)
            sections.push('```')
            break
          case 'csv':
            sections.push(csvToMarkdownTable(config.content))
            break
          case 'url':
            sections.push(`[${config.content}](${config.content})`)
            break
          default:
            sections.push('```')
            sections.push(config.content)
            sections.push('```')
        }
      })
    }

    sections.push('\n---\n')
    return sections.join('\n')
  } catch (error) {
    return `## LLM Response Text\n\nError parsing results: ${error}\n\n---\n`
  }
}

export const NativeLlmResponseTextDisplay = createWorkflowToolComponent(
  NativeLlmResponseTextDisplayComponent,
  downloadLlmResponse,
  nativeLlmResponseTextToMarkdown
)
